import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Check, X, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck, RiskPill } from "@/components/common/TrustBadge";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";
import { timeAgo } from "@/lib/format";

function RequestRow({ req }) {
  const qc = useQueryClient();
  const respond = useMutation({
    mutationFn: (action) => api.respondConnection(req.id, action),
    onSuccess: (_d, action) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      toast.success(action === "accept" ? "Connection accepted" : "Request declined");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Something went wrong"),
  });

  const s = req.sender;
  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4" data-testid={`request-${req.id}`}>
      <div className="flex items-start gap-3">
        <Avatar user={s} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{s?.display_name}</span>
            {s?.verified && <VerifiedCheck size={13} />}
            <TopConnectorCrown match={s} iconOnly />
            <RiskPill risk={req.risk} />
          </div>
          <div className="text-xs text-bloq-muted">
            @{s?.username} · Score {s?.score} · {timeAgo(req.created_at)}
          </div>
          {req.intro && (
            <p className="mt-2 rounded-xl bg-bloq-s2 px-3 py-2 text-sm text-white/80">“{req.intro}”</p>
          )}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => respond.mutate("accept")}
          disabled={respond.isPending}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full bloq-gradient py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50"
          data-testid={`accept-request-${req.id}`}
        >
          {respond.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Accept
        </button>
        <button
          onClick={() => respond.mutate("decline")}
          disabled={respond.isPending}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/12 py-2.5 text-sm font-semibold text-bloq-muted transition-colors hover:text-white disabled:opacity-50"
          data-testid={`decline-request-${req.id}`}
        >
          <X className="h-4 w-4" /> Decline
        </button>
      </div>
    </div>
  );
}

export default function RequestsPage() {
  const navigate = useNavigate();
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["requests"],
    queryFn: api.listRequests,
  });

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/app/chats")}
            className="rounded-full p-1 text-bloq-muted hover:text-white"
            data-testid="requests-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Connection requests</h1>
            <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
              <ShieldCheck className="h-3 w-3" /> Shield screens every request
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-16 text-bloq-muted">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-20 text-center">
              <UserPlus className="mx-auto mb-3 h-12 w-12 text-bloq-muted/50" />
              <p className="text-bloq-muted">No pending requests. You're all caught up.</p>
            </div>
          ) : (
            requests.map((req) => <RequestRow key={req.id} req={req} />)
          )}
        </div>
      </div>
    </div>
  );
}
